import { useQueryClient } from '@tanstack/react-query'
import {
  SELLER_ENTITY_LABELS_RU,
  SellerEntity,
  isSellerEntity,
  type BankAccountDto,
} from '@crm/shared'
import { updateBankAccount } from '../../shared/api/bank.api'
import { apiErrorText } from '../../shared/api/errorText'
import { toast } from '../../shared/ui/toastStore'
import { BANK_ACCOUNTS_KEY, BANK_STATEMENTS_KEY } from './bankFormat'

/**
 * Расчётные счета, по которым уже загружали выписки. Счёт заводится сам при первой
 * загрузке; юрлицо указывает бухгалтер — номера счетов 1С свои у каждого юрлица,
 * без юрлица поступление к счёту заказа не привязать.
 */
export function BankAccountsCard({
  accounts,
  canEdit,
}: {
  accounts: BankAccountDto[]
  canEdit: boolean
}) {
  const qc = useQueryClient()
  if (accounts.length === 0) return null

  const change = async (account: BankAccountDto, value: string) => {
    try {
      await updateBankAccount(account.id, isSellerEntity(value) ? value : null)
      void qc.invalidateQueries({ queryKey: [...BANK_ACCOUNTS_KEY] })
      void qc.invalidateQueries({ queryKey: [...BANK_STATEMENTS_KEY] })
      toast.success('Юрлицо расчётного счёта сохранено')
    } catch (e) {
      toast.error(apiErrorText(e, 'Не удалось сохранить юрлицо счёта'))
    }
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <h2 className="text-sm font-medium text-gray-900">Расчётные счета</h2>
      <div className="mt-3 space-y-2">
        {accounts.map((a) => (
          <div key={a.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <span className="font-mono text-gray-900">{a.accountNumber}</span>
            <span className="text-gray-500">
              {[a.ownerName, a.bankName].filter(Boolean).join(' · ') || '—'}
            </span>
            {canEdit ? (
              <select
                value={a.entityId ?? ''}
                onChange={(e) => void change(a, e.target.value)}
                aria-label={`Юрлицо счёта ${a.accountNumber}`}
                className={`rounded-lg border px-2 py-1 text-sm ${
                  a.entityId ? 'border-gray-300' : 'border-orange-400 bg-orange-50'
                }`}
              >
                <option value="">Юрлицо не указано</option>
                {Object.values(SellerEntity).map((code) => (
                  <option key={code} value={code}>
                    {SELLER_ENTITY_LABELS_RU[code]}
                  </option>
                ))}
              </select>
            ) : (
              <span className={a.entityId ? 'text-gray-700' : 'text-orange-600'}>
                {a.entityId ? SELLER_ENTITY_LABELS_RU[a.entityId] : 'Юрлицо не указано'}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
